import axios from "axios";

import { createAuthorizedClient, GOLDBOT_BASE_URL } from "./apiClient";

const resourceClient = createAuthorizedClient({
  baseURL: GOLDBOT_BASE_URL,
  timeout: 4500,
});

export const GoldBotResourceErrorKind = Object.freeze({
  UNAVAILABLE: "unavailable",
  NETWORK: "network",
  INVALID_RESPONSE: "invalid_response",
  REQUEST: "request",
});

export class GoldBotResourceError extends Error {
  constructor(message, kind, cause) {
    super(message);
    this.name = "GoldBotResourceError";
    this.kind = kind;
    this.cause = cause;
  }
}

export function isGoldBotResourceRequestCanceled(error) {
  return axios.isCancel(error);
}

export function isPlainObject(payload) {
  return (
    Boolean(payload) && typeof payload === "object" && !Array.isArray(payload)
  );
}

/**
 * Build a reader for one GoldBot endpoint using the same request, validation,
 * and error-classification rules as the /status and /positions services.
 */
export function createGoldBotResourceReader({ path, label, isValidPayload }) {
  return async function readGoldBotResource({ signal } = {}) {
    try {
      const response = await resourceClient.get(path, { signal });

      if (!isValidPayload(response.data)) {
        throw new GoldBotResourceError(
          `GoldBot returned an invalid ${label} response.`,
          GoldBotResourceErrorKind.INVALID_RESPONSE
        );
      }

      return response.data;
    } catch (error) {
      if (
        isGoldBotResourceRequestCanceled(error) ||
        error instanceof GoldBotResourceError
      ) {
        throw error;
      }

      if (error.response?.status === 503) {
        throw new GoldBotResourceError(
          `GoldBot ${label} are unavailable.`,
          GoldBotResourceErrorKind.UNAVAILABLE,
          error
        );
      }

      if (!error.response) {
        throw new GoldBotResourceError(
          `Could not reach the GoldBot ${label} API.`,
          GoldBotResourceErrorKind.NETWORK,
          error
        );
      }

      throw new GoldBotResourceError(
        `GoldBot ${label} request failed.`,
        GoldBotResourceErrorKind.REQUEST,
        error
      );
    }
  };
}
